import { GooglePlace } from "../../types"

/**
 * Determines if location details fields should be displayed
 * Location details are shown when:
 * - A Google Place has been selected (address searched), OR
 * - At least one location field (city, state, or zipcode) has a value
 */
export const shouldShowLocationDetails = (
  googlePlace?: GooglePlace,
  city?: string,
  state?: string,
  zipcode?: string
): boolean => {
  return !!googlePlace || !!(city || state || zipcode)
}

/**
 * Validates zipcode field conditionally
 * Zipcode is required only when location details should be shown
 *
 * @param googlePlace - The selected Google Place object (if address was searched)
 * @param city - City value from form
 * @param state - State value from form
 * @param zipcode - Zipcode value from form
 * @returns Error message string if validation fails, undefined if valid
 */
export const validateZipcodeConditionally = (
  googlePlace?: GooglePlace,
  city?: string,
  state?: string,
  zipcode?: string
): string | undefined => {
  if (
    shouldShowLocationDetails(googlePlace, city, state, zipcode) &&
    !zipcode
  ) {
    return "Zipcode is required"
  }
  return undefined
}

/**
 * Type guard to check if a payload has location fields
 */
interface HasLocationFields {
  city?: string
  state?: string
  zipcode?: string
}

export const validateZipcodeForPayload = <T extends HasLocationFields>(
  values: T,
  googlePlace?: GooglePlace
): Partial<Record<keyof T, string>> => {
  const errors: Partial<Record<keyof T, string>> = {}

  const zipcodeError = validateZipcodeConditionally(
    googlePlace,
    values.city,
    values.state,
    values.zipcode
  )

  if (zipcodeError) {
    errors.zipcode = zipcodeError as any
  }

  return errors
}
